import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, PayoutRow, PayoutTransactionRow } from '../api';
import { financeEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Panel, Table, formatWhen } from '../ui';
import type { Tone } from '../ui';
import { INPUT, dollars, humanize } from './event/shared';

/**
 * Finance → Payouts (D-64): every Stripe payout, what it settled and
 * whether the ledger agrees to the cent. Sync from Stripe, classify what
 * Stripe charged on its own, sign off when it ties.
 */

const tone = (status: string): Tone => (status === 'RECONCILED' ? 'ok' : status === 'MATCHED' ? 'accent' : status === 'ERROR' ? 'bad' : status === 'UNMATCHED' ? 'warn' : 'neutral');

export default function PayoutsPage() {
  const { id } = useParams();
  const can = usePermissions();
  if (!can('finance.reconciliation.view')) return <NotPermitted permission="finance.reconciliation.view" />;
  if (id) return <PayoutDetail id={id} />;
  return <PayoutList />;
}

function PayoutList() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const payouts = useQuery({ queryKey: ['payouts', status], queryFn: () => financeEndpoints.payouts(status || undefined) });
  const sync = useMutation({
    mutationFn: () => financeEndpoints.sync(35),
    onSuccess: (r) => {
      setNotice(`Synced ${r.synced.payouts} payout${r.synced.payouts === 1 ? '' : 's'}, ${r.synced.transactions} transactions, ${r.synced.matched} matched.`);
      void queryClient.invalidateQueries({ queryKey: ['payouts'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <Panel
        title="Stripe payouts"
        description="Each payout lists every balance transaction Stripe put in it, matched to the payment, refund or dispute it settles. Net must equal the ledger's Stripe Clearing movement for those rows; anything Stripe charged on its own is classified into a cost before the payout can be signed off. Payouts sync hourly and on Stripe's payout webhooks."
        actions={
          <div className="flex items-center gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={INPUT} aria-label="Status">
              <option value="">All</option>
              {['UNMATCHED', 'PARTIALLY_MATCHED', 'MATCHED', 'RECONCILED', 'ERROR'].map((s) => (
                <option key={s} value={s}>
                  {humanize(s)}
                </option>
              ))}
            </select>
            {can('finance.reconciliation.manage') ? (
              <Button variant="primary" disabled={sync.isPending} onClick={() => sync.mutate()}>
                {sync.isPending ? 'Syncing…' : 'Sync from Stripe'}
              </Button>
            ) : null}
          </div>
        }
      >
        <Table headers={['Arrives', 'Payout', 'Stripe', 'Charges', 'Refunds', 'Disputes', 'Fees', 'Other', 'Net', 'Ledger', 'Difference', 'Status']}>
          {(payouts.data?.payouts ?? []).length === 0 ? (
            <EmptyRow columns={12}>{payouts.data ? 'No payouts synced yet.' : 'Loading…'}</EmptyRow>
          ) : (
            payouts.data!.payouts.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{p.arrival_date}</td>
                <td className="px-5 py-3 font-mono text-xs">
                  <Link to={`/payouts/${p.id}`} className="hover:underline">
                    {p.stripe_payout_id}
                  </Link>
                  <div className="text-ink-400">
                    {p.matched_count}/{p.balance_transaction_count} matched
                  </div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{p.stripe_status}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.gross_charges_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.refunds_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.disputes_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">−{dollars(p.fees_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.adjustments_cents + p.other_cents)}</td>
                <td className="tabular px-5 py-3 text-sm font-semibold">{dollars(p.net_cents)}</td>
                <td className="tabular px-5 py-3 text-sm">{p.ledger_expected_net_cents === null ? '—' : dollars(p.ledger_expected_net_cents)}</td>
                <td className={`tabular px-5 py-3 text-sm ${p.difference_cents ? 'font-semibold text-bad' : 'text-ink-400'}`}>{p.difference_cents === null ? '—' : dollars(p.difference_cents)}</td>
                <td className="px-5 py-3">
                  <Badge tone={tone(p.reconciliation_status)}>{humanize(p.reconciliation_status)}</Badge>
                  {p.reconciled_by ? <div className="text-xs text-ink-400">{p.reconciled_by}</div> : null}
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function PayoutDetail({ id }: { id: string }) {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [classifying, setClassifying] = useState<{ id: string; code: string; reason: string } | null>(null);
  const detail = useQuery({ queryKey: ['payouts', 'detail', id], queryFn: () => financeEndpoints.payout(id) });
  const list = useQuery({ queryKey: ['payouts', ''], queryFn: () => financeEndpoints.payouts() });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['payouts'] });
  };
  const resync = useMutation({ mutationFn: () => financeEndpoints.resync(id), onSuccess: () => { setNotice('Re-synced from Stripe.'); refresh(); }, onError: (cause: ApiError) => setError(cause.message) });
  const signOff = useMutation({ mutationFn: () => financeEndpoints.signOff(id), onSuccess: () => { setNotice('Reconciled.'); refresh(); }, onError: (cause: ApiError) => setError(cause.message) });
  const classify = useMutation({
    mutationFn: (input: { id: string; code: string; reason: string }) => financeEndpoints.classify(input.id, { accounting_code: input.code, reason: input.reason }),
    onSuccess: () => {
      setClassifying(null);
      setNotice('Classified; the payout was recomputed.');
      refresh();
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  if (detail.error) return <ErrorNote>{(detail.error as ApiError).message}</ErrorNote>;
  if (!detail.data) return <p className="text-sm text-ink-400">Loading…</p>;
  const { payout: p, transactions, journal } = detail.data;
  const manage = can('finance.reconciliation.manage');
  return (
    <div className="space-y-4">
      <Link to="/payouts" className="text-sm text-ink-600 hover:underline">
        ← Payouts
      </Link>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{notice}</p> : null}
      <Panel
        title={`Payout ${p.stripe_payout_id}`}
        description={`Arrives ${p.arrival_date} · Stripe says ${p.stripe_status} · ${p.automatic ? 'automatic' : 'manual'}${p.destination_last4 ? ` · bank ····${p.destination_last4}` : ''}${journal ? ` · posted to the ledger on ${journal.business_date}` : ' · not posted yet'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={tone(p.reconciliation_status)}>{humanize(p.reconciliation_status)}</Badge>
            {manage ? (
              <Button disabled={resync.isPending} onClick={() => resync.mutate()}>
                Re-sync
              </Button>
            ) : null}
            {can('finance.payouts.sign_off') && p.reconciliation_status === 'MATCHED' ? (
              <Button variant="primary" disabled={signOff.isPending} onClick={() => signOff.mutate()}>
                Sign off
              </Button>
            ) : null}
          </div>
        }
      >
        <div className="grid gap-4 px-5 py-4 text-sm sm:grid-cols-3 lg:grid-cols-6">
          {[
            ['Charges', p.gross_charges_cents],
            ['Refunds', p.refunds_cents],
            ['Disputes', p.disputes_cents],
            ['Stripe fees', -p.fees_cents],
            ['Other', p.adjustments_cents + p.other_cents],
            ['Net', p.net_cents],
          ].map(([label, cents]) => (
            <div key={label as string}>
              <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">{label}</p>
              <p className="tabular mt-1 text-xl font-bold text-ink-900">{dollars(cents as number)}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-6 border-t border-line px-5 py-3 text-sm">
          <span>
            Ledger expects <span className="tabular font-semibold">{p.ledger_expected_net_cents === null ? '—' : dollars(p.ledger_expected_net_cents)}</span>
          </span>
          <span className={p.difference_cents ? 'font-semibold text-bad' : 'text-ok'}>Difference {p.difference_cents === null ? '—' : dollars(p.difference_cents)}</span>
          <span className="text-ink-600">
            {p.matched_count} of {p.balance_transaction_count} transactions matched
          </span>
          {p.reconciled_at ? <span className="text-ink-600">Signed off {formatWhen(p.reconciled_at)}</span> : null}
          {p.error ? <span className="text-bad">{p.error}</span> : null}
        </div>
      </Panel>

      <Panel title="Transactions" description="Every balance transaction in the payout. A charge or refund matches through the Stripe ids Ticketing recorded; an unmatched one is something Stripe charged on its own and is classified into an accounting code here.">
        <Table headers={['When', 'Type', 'Matched as', 'Order / refund', 'Description', 'Amount', 'Fee', 'Net', '']}>
          {transactions.length === 0 ? (
            <EmptyRow columns={9}>No transactions synced.</EmptyRow>
          ) : (
            transactions.map((t) => (
              <TransactionRow key={t.id} t={t} manage={manage && p.reconciliation_status !== 'RECONCILED'} classifying={classifying} setClassifying={setClassifying} codes={list.data?.accounting_codes ?? []} onClassify={(input) => classify.mutate(input)} busy={classify.isPending} />
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function TransactionRow({ t, manage, classifying, setClassifying, codes, onClassify, busy }: { t: PayoutTransactionRow; manage: boolean; classifying: { id: string; code: string; reason: string } | null; setClassifying: (v: { id: string; code: string; reason: string } | null) => void; codes: { code: string; name: string; family: string }[]; onClassify: (input: { id: string; code: string; reason: string }) => void; busy: boolean }) {
  const editing = classifying?.id === t.id;
  return (
    <tr className="border-b border-line last:border-0 align-top">
      <td className="px-5 py-3 text-sm whitespace-nowrap text-ink-600">{formatWhen(t.stripe_created_at)}</td>
      <td className="px-5 py-3 text-sm text-ink-900">
        {t.type}
        <div className="font-mono text-xs text-ink-400">{t.stripe_balance_transaction_id}</div>
      </td>
      <td className="px-5 py-3">
        <Badge tone={t.link_type ? (t.link_type === 'FEE_ADJUSTMENT' ? 'accent' : 'ok') : 'warn'}>{t.link_type ? humanize(t.link_type) : 'Unmatched'}</Badge>
      </td>
      <td className="px-5 py-3 text-sm">
        {t.order_id ? (
          <Link to={`/orders/${t.order_id}`} className="font-mono hover:underline">
            {t.order_number}
          </Link>
        ) : null}
        {t.refund_number ? <div className="font-mono text-xs text-ink-400">{t.refund_number}</div> : null}
        {t.event_title ? <div className="text-xs text-ink-400">{t.event_title}</div> : null}
      </td>
      <td className="px-5 py-3 text-sm text-ink-600">
        {t.description ?? ''}
        {t.source_id ? <div className="font-mono text-xs text-ink-400">{t.source_id}</div> : null}
      </td>
      <td className="tabular px-5 py-3 text-sm">{dollars(t.amount_cents)}</td>
      <td className="tabular px-5 py-3 text-sm">{t.fee_cents ? `−${dollars(t.fee_cents)}` : '—'}</td>
      <td className="tabular px-5 py-3 text-sm font-semibold">{dollars(t.net_cents)}</td>
      <td className="px-5 py-3 text-right">
        {!t.link_type && manage ? (
          editing ? (
            <div className="flex flex-col gap-1">
              <select value={classifying!.code} onChange={(e) => setClassifying({ ...classifying!, code: e.target.value })} className={INPUT} aria-label="Accounting code">
                <option value="">Code…</option>
                {codes.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name} ({c.family.toLowerCase()})
                  </option>
                ))}
              </select>
              <input value={classifying!.reason} onChange={(e) => setClassifying({ ...classifying!, reason: e.target.value })} placeholder="Reason" className={INPUT} aria-label="Reason" />
              <div className="flex gap-1">
                <Button variant="primary" disabled={busy || !classifying!.code || !classifying!.reason.trim()} onClick={() => onClassify(classifying!)}>
                  Classify
                </Button>
                <Button onClick={() => setClassifying(null)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setClassifying({ id: t.id, code: 'STRIPE_PROCESSING_COST', reason: '' })}>Classify…</Button>
          )
        ) : null}
      </td>
    </tr>
  );
}
